import { useState } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { formatCurrency } from '@opendoor/shared-fe/formatUtils';
import { IconChevronDown } from '@tabler/icons-react';

import type { MarketplaceProperty } from '../types';

import styles from './PdpPriceHistory.module.css';

const historyLabels = {
  COMING_SOON: 'Coming soon',
  FOR_SALE: 'Listed for sale',
  OFF_MARKET: 'Taken off market',
  SALE_PENDING: 'Sale pending',
  SOLD: 'Sold',
  UNKNOWN: 'Listing updated',
  WITHDRAWN: 'Listing withdrawn',
} satisfies Record<MarketplaceProperty['listingHistory'][number]['state'], string>;

const pricedHistoryLabel = (
  state: MarketplaceProperty['listingHistory'][number]['state'],
  statusLabel: string,
  price: string,
) => {
  if (state === 'FOR_SALE') return `Listed for ${price}`;
  if (state === 'SOLD') return `${statusLabel} for ${price}`;
  if (state === 'UNKNOWN') return `${statusLabel} to ${price}`;
  return `${statusLabel} at ${price}`;
};

export const PdpPriceHistory = ({
  listingHistory,
}: Pick<MarketplaceProperty, 'listingHistory'>) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const events = listingHistory.flatMap((event) => {
    if (!event.timestamp || (event.state === 'SALE_PENDING' && !event.mls)) return [];
    const date = new Date(event.timestamp);
    if (!Number.isFinite(date.getTime())) return [];
    return [{ ...event, date }];
  });

  const canCollapse = events.length > 1;

  const rows = events.map((event, index) => {
    const offMarket = event.state === 'OFF_MARKET' || event.state === 'WITHDRAWN';
    const priceCents =
      event.state === 'SOLD'
        ? event.soldPriceCents
        : (event.currentListPriceCents ?? event.originalListPriceCents);
    const hasPrice = !offMarket && priceCents != null;
    const statusLabel = historyLabels[event.state];
    const eventLabel = hasPrice
      ? pricedHistoryLabel(event.state, statusLabel, formatCurrency(priceCents / 100))
      : statusLabel;
    return (
      <Row
        role="listitem"
        aria-hidden={!isExpanded && index >= 2 ? true : undefined}
        key={`${event.timestamp}-${event.state}-${index}`}
        className={cn(
          styles.row,
          event.mls && styles.withMls,
          index === 1 && 'relative',
          index === 0 && styles.firstRow,
          index === events.length - 1 && styles.lastRow,
        )}
      >
        <time
          dateTime={event.date.toISOString()}
          aria-label={event.date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            timeZone: 'UTC',
          })}
          className="flex items-center gap-2 text-od-content-primary"
        >
          <Text
            as="span"
            className={cn(
              'flex items-center justify-center rounded-full px-2 text-body-xs',
              styles.year,
              index === 0
                ? cn(styles.currentYear, 'text-od-content-primary')
                : cn(styles.previousYear, 'text-od-content-secondary'),
            )}
          >
            {event.date.getUTCFullYear()}
          </Text>
          <Text as="span" className={cn('whitespace-nowrap text-body-xs', styles.date)}>
            {event.date.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              timeZone: 'UTC',
            })}
          </Text>
        </time>
        <Text className={cn('text-body-sm text-od-content-primary', styles.event)}>
          {eventLabel}
        </Text>
        {event.mls && (
          <Text
            className={cn(
              'whitespace-nowrap text-body-xs text-od-content-tertiary sm:text-od-content-secondary',
              styles.details,
            )}
          >
            {event.mls}
          </Text>
        )}
        {canCollapse && index === 1 && (
          <Box
            aria-hidden="true"
            data-expanded={isExpanded}
            className={cn(
              'pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-od-bg-primary/75 to-od-bg-primary backdrop-blur-[1px] [mask-image:linear-gradient(to_bottom,transparent,black)]',
              styles.historyFade,
              isExpanded && styles.historyFadeHidden,
            )}
          />
        )}
      </Row>
    );
  });

  return (
    <Col as="section" aria-label="Price history" className="gap-6">
      <Text as="h2" className="text-label-lg">
        Price History
      </Text>
      {events.length === 0 ? (
        <Text className="text-body-md text-od-content-secondary">No price history</Text>
      ) : (
        <Col className="w-full items-center gap-2">
          <Col id="pdp-price-history-events" role="list" className="w-full">
            {rows.slice(0, 2)}
            {rows.length > 2 && (
              <Box
                className={cn(styles.historyOverflow, isExpanded && styles.historyOverflowExpanded)}
              >
                <Col className="min-h-0 overflow-hidden">{rows.slice(2)}</Col>
              </Box>
            )}
          </Col>
          {canCollapse && (
            <Button
              variant="secondary"
              size="sm"
              analyticsName="pdp-refresh-price-history-toggle"
              aria-label={isExpanded ? 'See less' : 'See more'}
              aria-expanded={isExpanded}
              aria-controls="pdp-price-history-events"
              onClick={() => setIsExpanded((expanded) => !expanded)}
              className="h-8 self-center border-0 bg-transparent px-2 !text-od-content-secondary shadow-none transition-colors duration-150 ease-out hover:bg-od-bg-hover-primary"
            >
              {isExpanded ? 'See less' : 'See more'}
              <IconChevronDown
                size={16}
                aria-hidden="true"
                className={`transition-transform duration-150 ease-out ${isExpanded ? 'rotate-180' : ''}`}
              />
            </Button>
          )}
        </Col>
      )}
    </Col>
  );
};
